import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedList, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { CardPackCategory } from '@sorare/core/src/__generated__/globalTypes';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { TeamAvatar } from '@sorare/core/src/components/club/TeamAvatar';

import type { PackDescription_cardPack } from './__generated__/index.graphql.ts';

const TeamLabel = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: var(--half-unit);
`;

const isStartingPlayersCategory = (category?: CardPackCategory | null) =>
  category === CardPackCategory.ONLY_STARTING_PLAYERS ||
  category === CardPackCategory.ONLY_STARTING_PLAYERS_TEMPLATE;

type Props = {
  cardPack: PackDescription_cardPack;
};

const TeamItem = ({
  team,
}: {
  team: PackDescription_cardPack['teams'][number];
}) => {
  return (
    <TeamLabel key={team.slug}>
      <TeamAvatar team={team} size={16} />
      {team.code}
    </TeamLabel>
  );
};

export const PackDescription = ({ cardPack }: Props) => {
  const { description, category, teams } = cardPack;

  if (isStartingPlayersCategory(category) && teams.length > 0) {
    return (
      <BodyS as="p" color="var(--c-nd-600)">
        {teams.length === 2 ? (
          <FormattedMessage
            defaultMessage="Only players from {team1} vs {team2}"
            values={{
              team1: <TeamItem team={teams[0]} />,
              team2: <TeamItem team={teams[1]} />,
            }}
          />
        ) : (
          <FormattedMessage
            defaultMessage="Only players from {teams}"
            values={{
              teams: (
                <FormattedList
                  type="conjunction"
                  value={teams.map(team => (
                    <TeamItem team={team} key={team.slug} />
                  ))}
                />
              ),
            }}
          />
        )}
      </BodyS>
    );
  }

  if (!description) return null;

  return (
    <BodyS as="p" color="var(--c-nd-600)">
      {description}
    </BodyS>
  );
};

PackDescription.fragments = {
  cardPack: gql`
    fragment PackDescription_cardPack on CardPack {
      slug
      description
      category
      teams {
        slug
        code
        ...TeamAvatar_team
      }
    }
    ${TeamAvatar.fragments.team}
  ` as TypedDocumentNode<PackDescription_cardPack>,
};
